import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Target, Calendar, DollarSign, Heart } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import Select from '../components/ui/Select.tsx';
import AsyncMemberSelect from '../components/ui/AsyncMemberSelect.tsx';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import toast from 'react-hot-toast';
import React from 'react';
import { PermissionGate } from '../components/PermissionGate';
import PhoneFilters from '../components/PhoneFilters';
import PaybillSelect from '../components/PaybillSelect';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const COLLECTION_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'cash', label: 'Cash' },
  { value: 'digital_wallet', label: 'Digital Wallet' },
  { value: 'cheque', label: 'Cheque' },
];

export default function WelfarePage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedWelfare, setSelectedWelfare] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [selectedMemberToRemove, setSelectedMemberToRemove] = useState(null);
  const [form, setForm] = useState({
    name: '',
    purpose: '',
    target_amount: '',
    target_date: '',
    collection_method: '',
    recommended_contribution_amount: '',
    min_contribution_amount: '',
    max_contribution_amount: '',
    member_ids: [],
    include_all_members: true,
  });
  const queryClient = useQueryClient();
  const { data: profile } = useGetProfile();
  const groupId = profile?.data?.current_group?.id;
  const isAdmin = useIsAdmin();
  const { can } = usePermissions();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showAcceptMemberModal, setShowAcceptMemberModal] = useState(false);
  const [selectedMemberToAccept, setSelectedMemberToAccept] = useState(null);
  const [showDeclineMemberModal, setShowDeclineMemberModal] = useState(false);
  const [selectedMemberToDecline, setSelectedMemberToDecline] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch members list - same approach as contribution form
  const { data: membersData } = useQuery({
    queryKey: ['members'],
    queryFn: async () => {
      const { data } = await api.get('/members?per_page=300');
      return data.data;
    },
  });

  const members = membersData || [];

  // Fetch welfare plan members for details modal
  const { data: welfareMembersData, isLoading: isLoadingMembers } = useQuery({
    queryKey: ['welfare-members', selectedWelfare?.id],
    queryFn: async () => {
      if (!selectedWelfare?.id) return null;
      const { data } = await api.get(`/welfare-plans/${selectedWelfare.id}/members`);
      return data;
    },
    enabled: !!selectedWelfare?.id,
  });

  const welfareMembers = welfareMembersData?.data || [];

  // Get available members (not already in the welfare plan)
  const availableMembers = members.filter(member => 
    !welfareMembers.some(welfareMember => welfareMember.member_id === member.id)
  );

  const [formError, setFormError] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [beneficiaryOption, setBeneficiaryOption] = useState(null);
  const createWelfare = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/welfare-plans', payload);
      return data;
    },
    onSuccess: () => {
      toast.success('Welfare plan created!');
      setShowAddModal(false);
      setForm({
        name: '', purpose: '', target_amount: '', target_date: '', collection_method: '', recommended_contribution_amount: '', min_contribution_amount: '', max_contribution_amount: '', member_ids: [], include_all_members: true
      });
      setSelectedMembers([]);
      setBeneficiaryOption(null);
      setFormError('');
      queryClient.invalidateQueries({ queryKey: ['welfare-plans'] });
      // Reset to first page after creating a new plan
      setCurrentPage(1);
    },
    onError: (error) => {
      setFormError(error?.response?.data?.message || 'Failed to create welfare plan');
    }
  });

  const addMemberToWelfare = useMutation({
    mutationFn: async ({ welfareId, memberId }) => {
      const { data } = await api.post(`/welfare-plans/${welfareId}/addmember`, { member_id: memberId });
      return data;
    },
    onSuccess: () => {
      toast.success('Member added successfully!');
      setShowAddMemberModal(false);
      queryClient.invalidateQueries({ queryKey: ['welfare-members', selectedWelfare?.id] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to add member');
    }
  });

  const removeMemberFromWelfare = useMutation({
    mutationFn: async ({ welfareId, memberId }) => {
      const { data } = await api.post(`/welfare-plans/${welfareId}/removemember`, { member_id: memberId });
      return data;
    },
    onSuccess: () => {
      toast.success('Member removed successfully!');
      setShowRemoveMemberModal(false);
      setSelectedMemberToRemove(null);
      queryClient.invalidateQueries({ queryKey: ['welfare-members', selectedWelfare?.id] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to remove member');
    }
  });

  const acceptMemberInvite = useMutation({
    mutationFn: async ({ welfareId, memberId }) => {
      const { data } = await api.post(`/welfare-plans/${welfareId}/members/${memberId}/accept`);
      return data;
    },
    onSuccess: () => {
      toast.success('Member invitation accepted successfully on their behalf!');
      queryClient.invalidateQueries({ queryKey: ['welfare-members', selectedWelfare?.id] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to accept member invitation');
    }
  });

  const declineMemberInvite = useMutation({
    mutationFn: async ({ welfareId, memberId, reason }) => {
      const { data } = await api.post(`/welfare-plans/${welfareId}/members/${memberId}/decline`, { reason });
      return data;
    },
    onSuccess: () => {
      toast.success('Member invitation declined successfully on their behalf!');
      queryClient.invalidateQueries({ queryKey: ['welfare-members', selectedWelfare?.id] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to decline member invitation');
    }
  });

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleMembersChange = (selected) => {
    setForm(f => ({ ...f, member_ids: selected ? selected.map(opt => opt.value) : [] }));
  };

  const handleViewDetails = (welfare) => {
    setSelectedWelfare(welfare);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedWelfare(null);
  };

  const handleAddMember = (memberId) => {
    addMemberToWelfare.mutate({
      welfareId: selectedWelfare.id,
      memberId: memberId
    });
  };

  const handleRemoveMember = (member) => {
    setSelectedMemberToRemove(member);
    setShowRemoveMemberModal(true);
  };

  const confirmRemoveMember = () => {
    if (selectedMemberToRemove) {
      removeMemberFromWelfare.mutate({
        welfareId: selectedWelfare.id,
        memberId: selectedMemberToRemove.member_id
      });
    }
  };

  const handleAcceptMemberInvite = (member) => {
    setSelectedMemberToAccept(member);
    setShowAcceptMemberModal(true);
  };

  const confirmAcceptMember = () => {
    if (selectedMemberToAccept) {
      acceptMemberInvite.mutate({
        welfareId: selectedWelfare.id,
        memberId: selectedMemberToAccept.member_id
      });
      setShowAcceptMemberModal(false);
      setSelectedMemberToAccept(null);
    }
  };

  const handleDeclineMemberInvite = (member) => {
    setSelectedMemberToDecline(member);
    setShowDeclineMemberModal(true);
  };

  const confirmDeclineMember = () => {
    if (selectedMemberToDecline) {
      declineMemberInvite.mutate({
        welfareId: selectedWelfare.id,
        memberId: selectedMemberToDecline.member_id,
        reason: declineReason || 'Declined by admin'
      });
      setShowDeclineMemberModal(false);
      setSelectedMemberToDecline(null);
      setDeclineReason('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.purpose || !groupId || !form.collection_method) {
      setFormError('Please fill all required fields.');
      return;
    }
    if (!form.target_amount && !form.target_date) {
      setFormError('Either target amount or target date is required.');
      return;
    }
    createWelfare.mutate({
      ...form,
      group_id: groupId,
      member_ids: form.include_all_members ? [] : form.member_ids,
    });
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['welfare-plans', currentPage, perPage, debouncedSearchTerm, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: perPage.toString(),
      });
      
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      
      const res = await api.get(`/welfare-plans?${params.toString()}`);
      return res.data;
    },
  });

  // Get pagination info from response
  const pagination = data?.meta || {};
  const welfarePlans = data?.data || [];
  
  // Separate active and closed plans
  const activePlans = welfarePlans.filter(plan => plan.status === 'open') || [];
  const closedPlans = welfarePlans.filter(plan => plan.status === 'closed') || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getContributionStatusColor = (status) => {
    switch (status) {
      case 'contributed':
        return 'bg-green-100 text-green-800';
      case 'accepted':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Welfare Plans</h1>
          <Link to="/welfare/requests" className="text-sm text-green-700 hover:underline mr-auto ml-4">Requests &amp; needy list</Link>
          {isAdmin && (
            <PermissionGate permission={PERMISSIONS.CREATE_WELFARE_PLANS}>
              <button
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                onClick={() => setShowAddModal(true)}
              >
                <Plus size={18} className="mr-2" />
                Add Welfare
              </button>
            </PermissionGate>
          )}
        </div>

      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-4">
        {/* Search Summary */}
        {(debouncedSearchTerm || statusFilter) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="text-sm text-blue-800">
                  {debouncedSearchTerm && `Searching for "${debouncedSearchTerm}"`}
                  {debouncedSearchTerm && statusFilter && ' and '}
                  {statusFilter && `Filtered by ${statusFilter} status`}
                </span>
              </div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setCurrentPage(1);
                }}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Clear filters
              </button>
            </div>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search welfare plans..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            {searchTerm !== debouncedSearchTerm && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
              </div>
            )}
          </div>
          
          <PhoneFilters count={statusFilter ? 1 : 0}>
          {/* Status Filter */}
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1); // Reset to first page when filtering
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="open">Active</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          {/* Per Page Selector */}
          <div className="sm:w-32">
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing per page
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={15}>15 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>
          </PhoneFilters>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-3 text-gray-600">Loading welfare plans...</span>
        </div>
      ) : error ? (
        <div className="text-red-500 text-center py-8">Failed to load welfare plans.</div>
      ) : activePlans.length === 0 && closedPlans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="mb-6">
            {debouncedSearchTerm || statusFilter ? (
              <svg className="w-20 h-20 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            ) : (
              <Heart size={80} className="text-green-400 mx-auto animate-pulse" />
            )}
          </div>
          <p className="text-lg text-gray-500 mb-4">
            {debouncedSearchTerm || statusFilter 
              ? `No welfare plans found matching your search criteria.`
              : 'No welfare plans have been created yet.'
            }
          </p>
          {debouncedSearchTerm || statusFilter ? (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setCurrentPage(1);
              }}
              className="flex items-center bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Clear Search
            </button>
          ) : isAdmin && (
            <PermissionGate permission={PERMISSIONS.CREATE_WELFARE_PLANS}>
              <button
                className="flex items-center bg-blue-600 text-white px-8 py-3 rounded-full shadow-lg text-lg font-semibold hover:scale-105 transform transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-green-200"
                onClick={() => setShowAddModal(true)}
              >
                <Plus size={24} className="mr-3" />
                Create Your First Welfare Plan
              </button>
            </PermissionGate>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {/* Active Plans Section */}
          {activePlans.length > 0 && (
            <div>
              <div className="flex items-center mb-6">
                <div className="w-2 h-8 bg-green-500 rounded-full mr-3"></div>
                <h2 className="text-xl font-bold text-gray-800">Active Welfare Plans</h2>
                <span className="ml-3 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  {activePlans.length} {activePlans.length === 1 ? 'plan' : 'plans'}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activePlans.map((welfare) => (
            <div key={welfare.id} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden group">
              {/* Header with gradient background */}
              <div className="bg-blue-600 p-4 text-white relative">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold truncate">{welfare.name}</h3>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${getStatusColor(welfare.status)}`}>
                      {welfare.status?.charAt(0).toUpperCase() + welfare.status?.slice(1)}
                    </span>
                  </div>
                  <Heart size={24} className="text-white/80 group-hover:scale-110 transition-transform" />
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Purpose */}
                <div>
                  <p className="text-gray-600 text-sm line-clamp-2">{welfare.purpose}</p>
                  {welfare.beneficiary && <p className="text-sm font-medium text-blue-700 mt-1">For: {welfare.beneficiary.name}</p>}
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  {welfare.target_amount && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center">
                        <Target size={16} className="text-green-600 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">Target</p>
                          <p className="font-semibold text-sm">{formatCurrency(welfare.target_amount)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center">
                      <Users size={16} className="text-blue-600 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">Members</p>
                        <p className="font-semibold text-sm">{welfare.total_members_count || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                {welfare.target_amount && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-500">Progress</span>
                      <span className="text-xs font-medium">{welfare.progress_percentage || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(welfare.progress_percentage || 0)}`}
                        style={{ width: `${Math.min(welfare.progress_percentage || 0, 100)}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{formatCurrency(welfare.total_contributions || 0)} raised</span>
                      <span>{welfare.days_remaining > 0 ? `${welfare.days_remaining} days left` : 'Expired'}</span>
                    </div>
                  </div>
                )}

                {/* Additional Info */}
                <div className="space-y-2">
                  {welfare.target_date && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar size={14} className="mr-2" />
                      <span>Target: {new Date(welfare.target_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  {welfare.recommended_contribution_amount && (
                    <div className="flex items-center text-sm text-gray-600">
                      {/* <DollarSign size={14} className="mr-2" /> */}
                      <span>Recommended: {formatCurrency(welfare.recommended_contribution_amount)}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 pt-2">
                  <button 
                    className="flex-1 bg-green-600 text-white text-sm py-2 px-3 rounded-md hover:bg-green-700 transition-colors"
                    onClick={() => handleViewDetails(welfare)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
              </div>
            </div>
          )}

          {/* Closed Plans Section */}
          {closedPlans.length > 0 && (
            <div>
              <div className="flex items-center mb-6">
                <div className="w-2 h-8 bg-green-600 rounded-full mr-3"></div>
                <h2 className="text-xl font-bold text-gray-800">Completed Welfare Plans</h2>
                <span className="ml-3 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  {closedPlans.length} {closedPlans.length === 1 ? 'plan' : 'plans'}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {closedPlans.map((welfare) => (
                  <div key={welfare.id} className="bg-green-50 rounded-xl shadow-lg border border-green-200 hover:shadow-xl transition-all duration-300 overflow-hidden group relative">
                    {/* Celebration Badge */}
                    <div className="absolute top-4 right-4 z-10">
                      <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                        🎉 Completed
                      </div>
                    </div>
                    
                    {/* Header with celebration gradient */}
                    <div className="bg-green-600 p-4 text-white relative overflow-hidden">
                      <div className="absolute inset-0 bg-black/10"></div>
                      <div className="relative flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-bold truncate">{welfare.name}</h3>
                          <div className="flex items-center mt-2">
                            <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white">
                              Closed
                            </span>
                            {welfare.closed_at && (
                              <span className="ml-2 text-xs text-white/80">
                                {new Date(welfare.closed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-white/80 group-hover:scale-110 transition-transform">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-4">
                      {/* Purpose */}
                      <div>
                        <p className="text-gray-700 text-sm line-clamp-2 font-medium">{welfare.purpose}</p>
                        {welfare.beneficiary && <p className="text-sm font-medium text-green-700 mt-1">For: {welfare.beneficiary.name}</p>}
                      </div>

                      {/* Final Results */}
                      <div className="bg-white/60 rounded-lg p-3 border border-green-200">
                        <h4 className="font-semibold text-green-800 text-sm mb-2">📊 Final Results</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-600">Total Raised</p>
                            <p className="font-bold text-green-600 text-sm">{formatCurrency(welfare.total_contributions || 0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Members</p>
                            <p className="font-bold text-green-600 text-sm">{welfare.total_members_count || 0}</p>
                          </div>
                        </div>
                        {welfare.target_amount && (
                          <div className="mt-2">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-gray-600">Target: {formatCurrency(welfare.target_amount)}</span>
                              <span className="text-xs font-medium text-green-600">{welfare.progress_percentage || 0}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-green-600"
                                style={{ width: `${Math.min(welfare.progress_percentage || 0, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Closure Info */}
                      {welfare.closure_reason && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start">
                            <svg className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                            </svg>
                            <div>
                              <p className="text-xs text-blue-800 font-medium mb-1">Closure Reason</p>
                              <p className="text-xs text-blue-700">{welfare.closure_reason}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Button */}
                      <div className="pt-2">
                        <button 
                          className="w-full bg-green-600 text-white text-sm py-2 px-3 rounded-md hover:bg-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                          onClick={() => handleViewDetails(welfare)}
                        >
                          <div className="flex items-center justify-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Details
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {pagination && pagination.last_page > 1 && (
        <div className="mt-8 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} welfare plans
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Previous Page Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={pagination.current_page <= 1}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                pagination.current_page <= 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                let pageNum;
                if (pagination.last_page <= 5) {
                  pageNum = i + 1;
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.current_page >= pagination.last_page - 2) {
                  pageNum = pagination.last_page - 4 + i;
                } else {
                  pageNum = pagination.current_page - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      pageNum === pagination.current_page
                        ? 'bg-green-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            {/* Next Page Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.min(pagination.last_page, prev + 1))}
              disabled={pagination.current_page >= pagination.last_page}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                pagination.current_page >= pagination.last_page
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Welfare Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-6">Add Welfare</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name <span className="text-red-500">*</span></label>
                <input type="text" name="name" className="w-full border rounded px-3 py-2" value={form.name} onChange={handleFormChange} required maxLength={255} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Purpose <span className="text-red-500">*</span></label>
                <textarea name="purpose" className="w-full border rounded px-3 py-2" value={form.purpose} onChange={handleFormChange} required maxLength={1000} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Target Amount (KES)</label>
                  <input type="number" name="target_amount" className="w-full border rounded px-3 py-2" value={form.target_amount} onChange={handleFormChange} min={1} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Target Date</label>
                  <input type="date" name="target_date" className="w-full border rounded px-3 py-2" value={form.target_date} onChange={handleFormChange} min={new Date().toISOString().split('T')[0]} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Collection Method <span className="text-red-500">*</span></label>
                <Select
                  name="collection_method"
                  value={COLLECTION_METHODS.find(opt => opt.value === form.collection_method)}
                  onChange={opt => setForm(f => ({ ...f, collection_method: opt?.value || '' }))}
                  options={COLLECTION_METHODS}
                  className="text-base"
                  classNamePrefix="select"
                  isSearchable={false}
                  placeholder="Select method..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Who is this for? <span className="text-gray-400 font-normal">(optional)</span></label>
                <AsyncMemberSelect
                  value={beneficiaryOption}
                  onChange={(opt) => { setBeneficiaryOption(opt); setForm(f => ({ ...f, beneficiary_member_id: opt ? opt.value : '' })); }}
                  isClearable
                  className="text-base"
                  classNamePrefix="select"
                  placeholder="Pick the member being helped..."
                />
                <p className="text-xs text-gray-500 mt-1">A member who is the beneficiary is never counted as owing for their own collection.</p>
              </div>
              <PaybillSelect value={form.mpesa_credential_id} onChange={(v) => setForm(f => ({ ...f, mpesa_credential_id: v }))} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Recommended Contribution</label>
                  <input type="number" name="recommended_contribution_amount" className="w-full border rounded px-3 py-2" value={form.recommended_contribution_amount} onChange={handleFormChange} min={1} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Contribution</label>
                  <input type="number" name="min_contribution_amount" className="w-full border rounded px-3 py-2" value={form.min_contribution_amount} onChange={handleFormChange} min={1} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Contribution</label>
                  <input type="number" name="max_contribution_amount" className="w-full border rounded px-3 py-2" value={form.max_contribution_amount} onChange={handleFormChange} min={1} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Include All Members</label>
                <input type="checkbox" name="include_all_members" checked={form.include_all_members} onChange={handleFormChange} className="ml-2" />
              </div>
              {!form.include_all_members && (
                <div>
                  <label className="block text-sm font-medium mb-1">Select Members</label>
                  <AsyncMemberSelect
                    isMulti
                    name="member_ids"
                    value={selectedMembers}
                    onChange={(selectedOptions) => {
                      setSelectedMembers(selectedOptions || []);
                      setForm({ ...form, member_ids: (selectedOptions || []).map(opt => opt.value) });
                    }}
                    className="text-base"
                    classNamePrefix="select"
                    placeholder="Select members..."
                  />
                </div>
              )}
              {/* SMS Notification Awareness */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      SMS Notifications
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        {form.include_all_members 
                          ? `All group members will receive an SMS invitation to join this welfare plan.`
                          : `${form.member_ids.length} selected member${form.member_ids.length !== 1 ? 's' : ''} will receive an SMS invitation to join this welfare plan.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {formError && <div className="text-red-600 text-sm">{formError}</div>}
              <div className="flex justify-end space-x-2 pt-2">
                <button type="button" className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700">Create Welfare</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedWelfare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-blue-600 p-6 text-white flex-shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold mb-2 flex items-center">
                    {selectedWelfare.name}
                    {isAdmin && (
                      <>
                        {can(PERMISSIONS.EDIT_WELFARE_PLANS) && (
                          <button
                            className="ml-3 p-1 rounded-full bg-white shadow hover:bg-blue-100 transition-colors border border-blue-200 flex items-center"
                            title="Edit Welfare Plan"
                            onClick={() => setShowEditModal(true)}
                          >
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-2.828 0L9 13zm-6 6h6" />
                            </svg>
                            <span className="ml-1 text-xs font-semibold text-blue-600 hidden sm:inline">Edit</span>
                          </button>
                        )}
                        {can(PERMISSIONS.CLOSE_WELFARE_PLANS) && (
                          <button
                            className="ml-3 p-1 rounded-full bg-red-600 shadow hover:bg-red-700 transition-colors border border-red-200 flex items-center"
                            title="Close Welfare Plan"
                            onClick={() => setShowCloseModal(true)}
                          >
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span className="ml-1 text-xs font-semibold text-white hidden sm:inline">Close Plan</span>
                          </button>
                        )}
                      </>
                    )}
                  </h3>
                  <div className="mt-2 flex items-center bg-gray-100 rounded px-4 py-2">
                    <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                    </svg>
                    <span className="text-lg font-medium text-gray-800 italic">{selectedWelfare.purpose}</span>
                  </div>
                  {selectedWelfare.status === 'open' && (
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                      {selectedWelfare.mpesa_credential && (
                        <span className="bg-white/90 text-gray-800 rounded px-2 py-1">Paybill: {selectedWelfare.mpesa_credential.account_name || selectedWelfare.mpesa_credential.paybill_number} · {selectedWelfare.mpesa_credential.paybill_number}</span>
                      )}
                      <Link to={`/pay-contribution?welfare_plan_id=${selectedWelfare.id}&welfare_name=${encodeURIComponent(selectedWelfare.name)}`} className="bg-white text-green-700 font-semibold rounded px-3 py-1 hover:bg-green-50">Pay into this plan</Link>
                    </div>
                  )}
                </div>
                <button 
                  onClick={handleCloseDetails}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 kpi-grid">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Users size={20} className="text-blue-600 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Total Members</p>
                        <p className="text-xl font-bold">{welfareMembers.length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Target size={20} className="text-green-600 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Target Amount</p>
                        <p className="text-xl font-bold">{formatCurrency(selectedWelfare.target_amount || 0)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <DollarSign size={20} className="text-green-600 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Total Raised</p>
                        <p className="text-xl font-bold">{formatCurrency(selectedWelfare.total_contributions || 0)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Calendar size={20} className="text-orange-600 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Days Left</p>
                        <p className="text-xl font-bold">{selectedWelfare.days_remaining || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                {selectedWelfare.target_amount && (
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Progress</span>
                      <span className="text-sm font-medium">{selectedWelfare.progress_percentage || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(selectedWelfare.progress_percentage || 0)}`}
                        style={{ width: `${Math.min(selectedWelfare.progress_percentage || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Members List */}
                <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold flex items-center">
                    <Users size={20} className="mr-2" />
                    Participating Members ({welfareMembers.length})
                  </h4>
                  {isAdmin && can(PERMISSIONS.MANAGE_WELFARE_PLAN_MEMBERS) && (
                    <button
                      onClick={() => setShowAddMemberModal(true)}
                      disabled={availableMembers.length === 0}
                      className="flex items-center bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                    >
                      <Plus size={16} className="mr-1" />
                      Add Member
                    </button>
                  )}
                </div>
                
                {isLoadingMembers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                    <span className="ml-3 text-gray-600">Loading members...</span>
                  </div>
                ) : welfareMembers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No members have joined this welfare plan yet.</p>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="responsive-table min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Member
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Phone
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Amount Contributed
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            {isAdmin && (
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Action
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {welfareMembers.map((member) => (
                            <tr key={member.id} className="hover:bg-gray-50">
                              <td data-label="Member" className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm">
                                    {member.member?.name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <div className="ml-3">
                                    <div className="text-sm font-medium text-gray-900">
                                      {member.member?.name || 'Unknown Member'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {member.member?.member_no || 'No ID'}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td data-label="Phone" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {member.member?.phone_number || '-'}
                              </td>
                              <td data-label="Status" className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getContributionStatusColor(member.contribution_status)}`}>
                                  {member.contribution_status_label || member.contribution_status || 'Unknown'}
                                </span>
                              </td>
                              <td data-label="Amount Contributed" className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {member.actual_contribution ? formatCurrency(member.actual_contribution) : '-'}
                              </td>
                              <td data-label="Date" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {member.contributed_at ? new Date(member.contributed_at).toLocaleDateString() : '-'}
                              </td>
                              {isAdmin && (
                                <td data-label="Action" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div className="flex space-x-2">
                                    {member.status === 'pending' && can(PERMISSIONS.MANAGE_WELFARE_PLAN_INVITES) && (
                                      <>
                                        <button 
                                          onClick={() => handleAcceptMemberInvite(member)}
                                          disabled={acceptMemberInvite.isPending}
                                          className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                          title="Accept invitation on behalf of member"
                                        >
                                          Accept
                                        </button>
                                        <button 
                                          onClick={() => handleDeclineMemberInvite(member)}
                                          disabled={declineMemberInvite.isPending}
                                          className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                          title="Decline invitation on behalf of member"
                                        >
                                          Decline
                                        </button>
                                      </>
                                    )}
                                    {can(PERMISSIONS.MANAGE_WELFARE_PLAN_MEMBERS) && (
                                      <button 
                                        onClick={() => handleRemoveMember(member)}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Summary by Status */}
                {welfareMembers.length > 0 && (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-3 border">
                      <div className="text-sm font-medium text-gray-600">Contributed</div>
                      <div className="text-lg font-bold text-green-600">
                        {welfareMembers.filter(m => m.contribution_status === 'contributed').length}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <div className="text-sm font-medium text-gray-600">Accepted</div>
                      <div className="text-lg font-bold text-blue-600">
                        {welfareMembers.filter(m => m.status === 'accepted').length}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <div className="text-sm font-medium text-gray-600">Pending</div>
                      <div className="text-lg font-bold text-yellow-600">
                        {welfareMembers.filter(m => m.status === 'pending').length}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <div className="text-sm font-medium text-gray-600">Declined</div>
                      <div className="text-lg font-bold text-red-600">
                        {welfareMembers.filter(m => m.status === 'declined').length}
                      </div>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end flex-shrink-0">
              <button 
                onClick={handleCloseDetails}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && selectedWelfare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-blue-600 p-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Add Member to Welfare</h3>
                  <p className="text-white/90 text-sm mt-1">{selectedWelfare.name}</p>
                </div>
                <button 
                  onClick={() => setShowAddMemberModal(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* SMS Notification Awareness */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      SMS Notification
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        The selected member will receive an SMS invitation to join this welfare plan.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Available Members</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Select a member to add to the welfare plan:
                </p>
              </div>

              {availableMembers.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={64} className="mx-auto mb-4 text-gray-300" />
                  <h4 className="text-lg font-semibold text-gray-700 mb-2">All Members Added</h4>
                  <p className="text-gray-500">All available members are already part of this welfare plan.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Select members to add to this welfare plan. They will receive an invitation to participate.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3">
                    {availableMembers.map((member) => (
                      <div 
                        key={member.id} 
                        className="flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{member.name}</div>
                            <div className="text-sm text-gray-500">{member.phone_number}</div>
                            {member.member_no && (
                              <div className="text-xs text-gray-400">ID: {member.member_no}</div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddMember(member.id)}
                          disabled={addMemberToWelfare.isPending}
                          className="flex items-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          {addMemberToWelfare.isPending ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          ) : (
                            <Plus size={16} className="mr-2" />
                          )}
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button 
                onClick={() => setShowAddMemberModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Modal */}
      {showRemoveMemberModal && selectedMemberToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
            {/* Header */}
            <div className="bg-red-600 p-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Remove Member</h3>
                  <p className="text-white/90 text-sm mt-1">Are you sure you want to remove this member?</p>
                </div>
                <button 
                  onClick={() => setShowRemoveMemberModal(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="h-16 w-16 rounded-full bg-red-600 flex items-center justify-center text-white font-medium text-xl">
                  {selectedMemberToRemove.member?.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="ml-4">
                  <div className="text-lg font-semibold text-gray-900">{selectedMemberToRemove.member?.name}</div>
                  <div className="text-sm text-gray-500">{selectedMemberToRemove.member?.phone_number}</div>
                  <div className="text-sm text-gray-500">Status: {selectedMemberToRemove.contribution_status_label || selectedMemberToRemove.contribution_status}</div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      This action will remove the member from the welfare plan.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button 
                  onClick={() => setShowRemoveMemberModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmRemoveMember}
                  disabled={removeMemberFromWelfare.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {removeMemberFromWelfare.isPending ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Removing...
                    </div>
                  ) : (
                    'Remove Member'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accept Member Invite Modal */}
      {showAcceptMemberModal && selectedMemberToAccept && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Accept Member Invitation</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Are you sure you want to accept the welfare plan invitation on behalf of{' '}
                  <span className="font-semibold text-gray-900">{selectedMemberToAccept.member?.name}</span>?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                    </svg>
                    <div>
                      <p className="text-sm text-blue-800 font-medium mb-1">What happens next:</p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Member will be notified via SMS/WhatsApp</li>
                        <li>• Member status will change to "Accepted"</li>
                        <li>• Member can now contribute to the welfare plan</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button 
                  onClick={() => setShowAcceptMemberModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmAcceptMember}
                  disabled={acceptMemberInvite.isPending}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {acceptMemberInvite.isPending ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Accepting...
                    </div>
                  ) : (
                    'Accept Invitation'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Member Invite Modal */}
      {showDeclineMemberModal && selectedMemberToDecline && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Decline Member Invitation</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Are you sure you want to decline the welfare plan invitation on behalf of{' '}
                  <span className="font-semibold text-gray-900">{selectedMemberToDecline.member?.name}</span>?
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for declining (optional)
                  </label>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Enter reason for declining..."
                    rows={3}
                  />
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div>
                      <p className="text-sm text-red-800 font-medium mb-1">What happens next:</p>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Member will be notified via SMS/WhatsApp</li>
                        <li>• Member status will change to "Declined"</li>
                        <li>• Member will no longer be able to participate</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button 
                  onClick={() => {
                    setShowDeclineMemberModal(false);
                    setDeclineReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDeclineMember}
                  disabled={declineMemberInvite.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {declineMemberInvite.isPending ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Declining...
                    </div>
                  ) : (
                    'Decline Invitation'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Welfare Plan Modal */}
      {showEditModal && selectedWelfare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-blue-600 p-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Edit Welfare Plan</h3>
                  <p className="text-white/90 text-sm mt-1">Update details for <span className='font-semibold'>{selectedWelfare.name}</span></p>
                </div>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <EditWelfareForm 
              welfare={selectedWelfare} 
              onClose={() => setShowEditModal(false)} 
              onSuccess={() => {
                setShowEditModal(false);
                queryClient.invalidateQueries({ queryKey: ['welfare-plans'] });
                queryClient.invalidateQueries({ queryKey: ['welfare-members', selectedWelfare.id] });
              }}
            />
          </div>
        </div>
      )}

      {/* Close Welfare Plan Modal */}
      {showCloseModal && selectedWelfare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="bg-red-600 p-6 text-white rounded-t-xl">
              <div className="flex items-center">
                <svg className="w-8 h-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <h3 className="text-xl font-bold">Close Welfare Plan</h3>
              </div>
            </div>
            {/* Content */}
            <div className="p-2 flex-1 overflow-y-auto">
              {/* Plan Summary */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
                <h4 className="font-bold text-lg text-gray-800 mb-2">📋 Plan Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Name:</span>
                    <p className="text-gray-800 font-semibold">{selectedWelfare.name}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Progress:</span>
                    <p className="text-gray-800 font-semibold">{selectedWelfare.progress_percentage}%</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Contributions:</span>
                    <p className="text-gray-800 font-semibold">KES {formatCurrency(selectedWelfare.total_contributions)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Members:</span>
                    <p className="text-gray-800 font-semibold">{selectedWelfare.participating_members_count}/{selectedWelfare.total_members_count}</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <h4 className="font-bold text-yellow-800 mb-1">⚠️ Important Notice</h4>
                    <p className="text-yellow-700 text-sm">Closing this plan will:</p>
                    <ul className="text-yellow-700 text-sm mt-2 space-y-1">
                      <li>• 🚫 Prevent new members from joining</li>
                      <li>• 💰 Stop accepting contributions</li>
                      <li>• 📧 Send closure notifications to all members</li>
                      <li>• 📊 Preserve all records for future reference</li>
                      <li>• 🎯 Mark the plan as completed</li>
                    </ul>
                  </div>
                </div>
              </div>

              <CloseWelfareForm 
                welfareId={selectedWelfare.id} 
                onSuccess={() => {
                  setShowCloseModal(false);
                  setShowDetailsModal(false);
                  queryClient.invalidateQueries({ queryKey: ['welfare-plans'] });
                }}
                onCancel={() => setShowCloseModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
}

function EditWelfareForm({ welfare, onClose, onSuccess }) {
  const [form, setForm] = React.useState({
    name: welfare.name || '',
    purpose: welfare.purpose || '',
    target_amount: welfare.target_amount || '',
    target_date: welfare.target_date ? welfare.target_date.split('T')[0] : '',
    collection_method: welfare.collection_method || '',
    recommended_contribution_amount: welfare.recommended_contribution_amount || '',
    min_contribution_amount: welfare.min_contribution_amount || '',
    max_contribution_amount: welfare.max_contribution_amount || '',
    mpesa_credential_id: welfare.mpesa_credential_id || '',
    beneficiary_member_id: welfare.beneficiary_member_id || '',
  });
  const [benOpt, setBenOpt] = React.useState(welfare.beneficiary ? { value: welfare.beneficiary.id, label: welfare.beneficiary.name } : null);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await api.put(`/welfare-plans/${welfare.id}`, form);
      setSuccess('Welfare plan updated!');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update welfare plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Name</label>
        <input type="text" name="name" className="w-full border rounded px-3 py-2" value={form.name} onChange={handleChange} required maxLength={255} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Purpose</label>
        <textarea name="purpose" className="w-full border rounded px-3 py-2" value={form.purpose} onChange={handleChange} required maxLength={1000} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Target Amount (KES)</label>
          <input type="number" name="target_amount" className="w-full border rounded px-3 py-2" value={form.target_amount} onChange={handleChange} min={1} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Target Date</label>
          <input type="date" name="target_date" className="w-full border rounded px-3 py-2" value={form.target_date} onChange={handleChange} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Collection Method</label>
        <Select
          name="collection_method"
          value={COLLECTION_METHODS.find(opt => opt.value === form.collection_method)}
          onChange={opt => setForm(f => ({ ...f, collection_method: opt?.value || '' }))}
          options={COLLECTION_METHODS}
          className="text-base"
          classNamePrefix="select"
          isSearchable={false}
          placeholder="Select method..."
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Who is this for? <span className="text-gray-400 font-normal">(optional)</span></label>
        <AsyncMemberSelect
          value={benOpt}
          onChange={(opt) => { setBenOpt(opt); setForm(f => ({ ...f, beneficiary_member_id: opt ? opt.value : '' })); }}
          isClearable
          className="text-base"
          classNamePrefix="select"
          placeholder="Pick the member being helped..."
        />
      </div>
      <PaybillSelect value={form.mpesa_credential_id} onChange={(v) => setForm(f => ({ ...f, mpesa_credential_id: v }))} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Recommended Contribution</label>
          <input type="number" name="recommended_contribution_amount" className="w-full border rounded px-3 py-2" value={form.recommended_contribution_amount} onChange={handleChange} min={1} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Min Contribution</label>
          <input type="number" name="min_contribution_amount" className="w-full border rounded px-3 py-2" value={form.min_contribution_amount} onChange={handleChange} min={1} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Max Contribution</label>
          <input type="number" name="max_contribution_amount" className="w-full border rounded px-3 py-2" value={form.max_contribution_amount} onChange={handleChange} min={1} />
        </div>
      </div>
      {error && <div className="text-red-600 text-sm">{error}</div>}
      {success && <div className="text-green-600 text-sm">{success}</div>}
      <div className="flex justify-end space-x-2 pt-2">
        <button type="button" className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={onClose} disabled={isSubmitting}>Cancel</button>
        <button type="submit" className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

function CloseWelfareForm({ welfareId, onSuccess, onCancel }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [showCelebration, setShowCelebration] = React.useState(false);

  const handleClose = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for closing the plan');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await api.post(`/welfare-plans/${welfareId}/close`, { reason });
      
      // Show celebration animation
      setShowCelebration(true);
      setTimeout(() => {
        toast.success('🎉 Welfare plan closed successfully!');
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to close welfare plan');
    } finally {
      setLoading(false);
    }
  };

  if (showCelebration) {
    return (
      <div className="text-center py-8">
        <div className="animate-bounce mb-4">
          <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-green-600 mb-2">Plan Closed Successfully!</h3>
        <p className="text-gray-600 mb-4">
          All members have been notified and the plan is now marked as completed. 
          You can view the final results in the completed plans section.
        </p>

        {/* Success Stats */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-green-600 font-semibold">✅</div>
              <div className="text-gray-600">Plan Closed</div>
            </div>
            <div>
              <div className="text-blue-600 font-semibold">📧</div>
              <div className="text-gray-600">Notifications Sent</div>
            </div>
            <div>
              <div className="text-green-600 font-semibold">📊</div>
              <div className="text-gray-600">Records Preserved</div>
            </div>
            <div>
              <div className="text-orange-600 font-semibold">🎯</div>
              <div className="text-gray-600">Mission Accomplished</div>
            </div>
          </div>
        </div>

        <button
          onClick={onCancel}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Reason Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <span className="text-red-600">*</span> Reason for Closing
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Please provide a reason for closing this welfare plan (e.g., 'Target amount reached', 'All members have contributed', 'Plan completed successfully')"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
          rows={3}
          required
        />
        <p className="text-xs text-gray-500 mt-1">This reason will be shared with all members in the closure notification.</p>
      </div>

      {/* Closure Effects Preview */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-800 mb-3">🔮 What happens next:</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center text-gray-600">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
            <span>Plan status changes to "Closed"</span>
          </div>
          <div className="flex items-center text-gray-600">
            <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
            <span>No new contributions accepted</span>
          </div>
          <div className="flex items-center text-gray-600">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
            <span>Email notifications sent to all members</span>
          </div>
          <div className="flex items-center text-gray-600">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
            <span>All records preserved for future reference</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          className="px-6 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition-colors"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          className={`px-6 py-2 rounded-lg font-medium transition-all transform hover:scale-105 ${
            reason.trim() 
              ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          onClick={handleClose}
          disabled={loading || !reason.trim()}
        >
          {loading ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Closing...
            </div>
          ) : (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close Plan
            </div>
          )}
        </button>
      </div>
    </div>
  );
}

// Creative Success Notification Component
function WelfarePlanClosedSuccess({ planName, onClose }) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  if (!showConfetti) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Confetti Animation */}
        <div className="relative h-32 bg-green-600 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random() * 2}s`
              }}
            >
              <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
            </div>
          ))}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="text-4xl mb-2">🎉</div>
              <h2 className="text-xl font-bold">Plan Completed!</h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {planName} has been successfully closed!
          </h3>
          <p className="text-gray-600 mb-6">
            All members have been notified and the plan is now marked as completed. 
            You can view the final results in the completed plans section.
          </p>

          {/* Success Stats */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-green-600 font-semibold">✅</div>
                <div className="text-gray-600">Plan Closed</div>
              </div>
              <div>
                <div className="text-blue-600 font-semibold">📧</div>
                <div className="text-gray-600">Notifications Sent</div>
              </div>
              <div>
                <div className="text-green-600 font-semibold">📊</div>
                <div className="text-gray-600">Records Preserved</div>
              </div>
              <div>
                <div className="text-orange-600 font-semibold">🎯</div>
                <div className="text-gray-600">Mission Accomplished</div>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
} 